import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Save,
  Trash2,
  LayoutGrid,
  Settings,
  RefreshCw,
  Edit2,
  Copy,
  FolderOpen,
  X,
  GripVertical,
  TrendingUp,
  TrendingDown,
  Zap,
  Activity,
  AlertTriangle,
  Volume2,
} from 'lucide-react';
import {
  ScannerConfig,
  ScannerResult,
  ScannerColumn,
  ScannerMetadata,
  ScannerType,
  DashboardLayout,
  DashboardPanel,
} from '@/services/scannerService';
import scannerApiService from '@/services/scannerService';
import signalRService from '@/services/signalRService';
import { CreateScannerDialog } from './CreateScannerDialog';
import { MiniScannerTable } from './DynamicScannerTable';

// ── Panel header colors ──
const HEADER_COLORS = [
  '#1e40af', '#059669', '#b91c1c', '#7c3aed',
  '#ca8a04', '#0891b2', '#be185d', '#4338ca',
];

const getTypeIcon = (type: ScannerType) => {
  switch (type) {
    case 'MomoHOD': case 'MomoUp2Min': case 'GapUp': case 'TopGainers':
      return <TrendingUp className="h-3 w-3 text-emerald-500" />;
    case 'MomoLOD': case 'MomoDown2Min': case 'GapDown': case 'TopLosers':
      return <TrendingDown className="h-3 w-3 text-red-400" />;
    case 'VolatilityHalt': case 'NewsHalt':
      return <AlertTriangle className="h-3 w-3 text-yellow-400" />;
    case 'VolumeSpike': case 'MostActive':
      return <Volume2 className="h-3 w-3 text-blue-400" />;
    default:
      return <Activity className="h-3 w-3 text-primary" />;
  }
};

// ── Mini Scanner Panel shown in the dashboard ──
interface MiniScannerPanelProps {
  scanner: ScannerConfig;
  results: ScannerResult[];
  columns?: ScannerColumn[];
  headerColor?: string;
  onSymbolClick?: (symbol: string) => void;
  onRemove?: () => void;
  isEditing: boolean;
}

const MiniScannerPanel: React.FC<MiniScannerPanelProps> = ({
  scanner, results, columns, headerColor, onSymbolClick, onRemove, isEditing,
}) => (
  <Card className="h-full flex flex-col bg-card/50 border-border/50 overflow-hidden relative group">
    {isEditing && onRemove && (
      <button
        className="absolute top-1 right-1 z-10 p-1 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={onRemove}
      >
        <X className="h-3 w-3" />
      </button>
    )}
    <div
      className="px-3 py-1.5 flex items-center gap-2 text-white text-xs font-semibold shrink-0"
      style={{ backgroundColor: headerColor ?? '#1e40af' }}
    >
      {getTypeIcon(scanner.type)}
      <span className="truncate">{scanner.name}</span>
      <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1">
        {results.length}
      </Badge>
    </div>
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <MiniScannerTable
        results={results}
        columns={columns}
        maxRows={15}
        onSymbolClick={onSymbolClick}
      />
    </div>
  </Card>
);

// ── Save / Load Dashboard Dialog ──
interface SaveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, description: string) => void;
  initialName?: string;
  initialDescription?: string;
}

const SaveDashboardDialog: React.FC<SaveDialogProps> = ({
  open, onOpenChange, onSave, initialName = '', initialDescription = '',
}) => {
  const [name, setName] = useState(initialName);
  const [desc, setDesc] = useState(initialDescription);

  useEffect(() => {
    if (open) { setName(initialName); setDesc(initialDescription); }
  }, [open, initialName, initialDescription]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Save Dashboard</DialogTitle>
          <DialogDescription>Give your dashboard a name to save it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <input
              className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="My Dashboard"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Description</label>
            <input
              className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Optional"
              value={desc}
              onChange={e => setDesc(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onSave(name, desc); onOpenChange(false); }} disabled={!name.trim()}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Add Scanner Panel Dialog ──
interface AddPanelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scanners: ScannerConfig[];
  onAdd: (scannerId: string, color: string) => void;
  onCreateNew: () => void;
}

const AddPanelDialog: React.FC<AddPanelDialogProps> = ({
  open, onOpenChange, scanners, onAdd, onCreateNew,
}) => {
  const [selectedScanner, setSelectedScanner] = useState('');
  const [color, setColor] = useState(HEADER_COLORS[0]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Scanner Panel</DialogTitle>
          <DialogDescription>Choose a scanner to add to the dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Scanner</label>
            <Select value={selectedScanner} onValueChange={setSelectedScanner}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select a scanner..." />
              </SelectTrigger>
              <SelectContent>
                {scanners.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} {s.isPreset ? '(Preset)' : '(Custom)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Header Color</label>
            <div className="flex gap-2">
              {HEADER_COLORS.map(c => (
                <button
                  key={c}
                  className={`w-7 h-7 rounded-md border-2 transition-all ${color === c ? 'border-white scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          <Button variant="outline" size="sm" className="w-full" onClick={onCreateNew}>
            <Plus className="h-3 w-3 mr-1" /> Create New Scanner Instead
          </Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onAdd(selectedScanner, color); onOpenChange(false); }} disabled={!selectedScanner}>
            Add Panel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═════════════════════════════════════════════════════
// ═══ Custom Dashboard Builder — Main Component ══════
// ═════════════════════════════════════════════════════
interface CustomDashboardProps {
  onSymbolSelect?: (symbol: string) => void;
}

export const CustomDashboard: React.FC<CustomDashboardProps> = ({ onSymbolSelect }) => {
  // ── State ──
  const [allScanners, setAllScanners] = useState<ScannerConfig[]>([]);
  const [dashboards, setDashboards] = useState<DashboardLayout[]>([]);
  const [activeDashboardId, setActiveDashboardId] = useState<string | null>(null);
  const [panels, setPanels] = useState<DashboardPanel[]>([]);
  const [columns, setColumns] = useState(3);
  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDesc, setDashboardDesc] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [scannerResults, setScannerResults] = useState<Record<string, ScannerResult[]>>({});
  const [scannerColumnMeta, setScannerColumnMeta] = useState<Record<string, ScannerColumn[]>>({});

  // Dialogs
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [createScannerOpen, setCreateScannerOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);

  const refreshInterval = useRef<ReturnType<typeof setInterval>>();
  const subscribedScannerIds = useRef<Set<string>>(new Set());

  // ── Load scanners + dashboards on mount ──
  useEffect(() => {
    loadScanners();
    loadDashboards();
  }, []);

  // ── SignalR scanner subscriptions (replaces HTTP polling) ──
  useEffect(() => {
    if (panels.length === 0) return;

    const panelScannerIds = panels.map(p => p.scannerId);

    // Handler for real-time scanner updates (dynamic data only)
    const handler = (_scannerId: string, update: any) => {
      const id = update.scannerId ?? _scannerId;
      if (panelScannerIds.includes(id)) {
        setScannerResults(prev => ({ ...prev, [id]: update.results ?? [] }));
      }
    };

    // Handler for one-time metadata (columns, sort, etc.)
    const metadataHandler = (_scannerId: string, metadata: ScannerMetadata) => {
      const id = metadata.scannerId ?? _scannerId;
      if (panelScannerIds.includes(id) && metadata.columns?.length) {
        setScannerColumnMeta(prev => ({ ...prev, [id]: metadata.columns }));
      }
    };

    signalRService.addScannerUpdateListener('custom-dashboard', handler);
    signalRService.addScannerMetadataListener('custom-dashboard', metadataHandler);

    // Subscribe to each panel's scanner via SignalR
    const setup = async () => {
      for (const panel of panels) {
        await signalRService.subscribeToScanner(panel.scannerId);
        subscribedScannerIds.current.add(panel.scannerId);
      }
    };
    setup();

    return () => {
      signalRService.removeScannerUpdateListener('custom-dashboard');
      signalRService.removeScannerMetadataListener('custom-dashboard');
      // Unsubscribe scanners no longer in panels
      for (const id of subscribedScannerIds.current) {
        if (!panelScannerIds.includes(id)) {
          signalRService.unsubscribeFromScanner(id);
          subscribedScannerIds.current.delete(id);
        }
      }
    };
  }, [panels]);

  const loadScanners = async () => {
    try {
      const scanners = await scannerApiService.getScanners();
      setAllScanners(scanners);
    } catch {
      // ignore
    }
  };

  const loadDashboards = async () => {
    try {
      const dbs = await scannerApiService.getDashboards();
      setDashboards(dbs);
    } catch {
      // ignore
    }
  };

  // ── Panel management ──
  const addPanel = useCallback((scannerId: string, color: string) => {
    const row = Math.floor(panels.length / columns);
    const col = panels.length % columns;
    setPanels(prev => [...prev, { scannerId, row, col, colSpan: 1, rowSpan: 1, headerColor: color }]);
  }, [panels.length, columns]);

  const removePanel = useCallback((index: number) => {
    setPanels(prev => prev.filter((_, i) => i !== index));
  }, []);

  // ── Save / Load ──
  const handleSave = useCallback(async (name: string, description: string) => {
    const layout: Partial<DashboardLayout> = {
      id: activeDashboardId ?? undefined,
      name,
      description,
      panels,
      columns,
      isDefault: false,
    };

    try {
      let saved: DashboardLayout;
      if (activeDashboardId) {
        saved = await scannerApiService.updateDashboard(activeDashboardId, layout);
      } else {
        saved = await scannerApiService.createDashboard(layout);
      }
      setActiveDashboardId(saved.id);
      setDashboardName(saved.name);
      setDashboardDesc(saved.description ?? '');
      await loadDashboards();
    } catch {
      // ignore
    }
  }, [activeDashboardId, panels, columns]);

  const handleLoad = useCallback(async (dashboardId: string) => {
    try {
      const db = await scannerApiService.getDashboard(dashboardId);
      setActiveDashboardId(db.id);
      setDashboardName(db.name);
      setDashboardDesc(db.description ?? '');
      setPanels(db.panels);
      setColumns(db.columns);
      setLoadDialogOpen(false);
    } catch {
      // ignore
    }
  }, []);

  const handleDelete = useCallback(async (dashboardId: string) => {
    try {
      await scannerApiService.deleteDashboard(dashboardId);
      if (activeDashboardId === dashboardId) {
        setActiveDashboardId(null);
        setPanels([]);
        setDashboardName('');
        setDashboardDesc('');
      }
      await loadDashboards();
    } catch {
      // ignore
    }
  }, [activeDashboardId]);

  const handleNewDashboard = useCallback(() => {
    setActiveDashboardId(null);
    setPanels([]);
    setDashboardName('');
    setDashboardDesc('');
    setColumns(3);
    setIsEditing(true);
  }, []);

  // ── Scanner creation callback ──
  const handleScannerCreated = useCallback(async (config: ScannerConfig) => {
    try {
      const created = await scannerApiService.createScanner(config);
      await loadScanners();
      // Auto-add the new scanner as a panel
      const color = HEADER_COLORS[panels.length % HEADER_COLORS.length];
      addPanel(created.id, color);
      setCreateScannerOpen(false);
    } catch {
      // ignore
    }
  }, [panels.length, addPanel]);

  const getScannerById = useCallback((id: string) => allScanners.find(s => s.id === id), [allScanners]);

  return (
    <div className="h-full flex flex-col">
      {/* ── Toolbar ── */}
      <div className="shrink-0 px-4 py-2 border-b border-border/50 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 mr-auto">
          <LayoutGrid className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">
            {dashboardName || 'Custom Dashboard'}
          </span>
          {dashboardDesc && (
            <span className="text-xs text-muted-foreground hidden sm:inline">— {dashboardDesc}</span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {/* Column selector */}
          <Select value={String(columns)} onValueChange={v => setColumns(Number(v))}>
            <SelectTrigger className="h-7 w-[80px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2">2 cols</SelectItem>
              <SelectItem value="3">3 cols</SelectItem>
              <SelectItem value="4">4 cols</SelectItem>
              <SelectItem value="5">5 cols</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAddPanelOpen(true)}>
            <Plus className="h-3 w-3 mr-1" /> Add Panel
          </Button>

          <Button
            variant={isEditing ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs"
            onClick={() => setIsEditing(!isEditing)}
          >
            <Edit2 className="h-3 w-3 mr-1" /> {isEditing ? 'Done' : 'Edit'}
          </Button>

          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setSaveDialogOpen(true)} disabled={panels.length === 0}>
            <Save className="h-3 w-3 mr-1" /> Save
          </Button>

          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setLoadDialogOpen(true)}>
            <FolderOpen className="h-3 w-3 mr-1" /> Load
          </Button>

          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleNewDashboard}>
            <Plus className="h-3 w-3 mr-1" /> New
          </Button>
        </div>
      </div>

      {/* ── Dashboard Grid ── */}
      <div className="flex-1 overflow-auto p-3">
        {panels.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-3">
            <LayoutGrid className="h-12 w-12 opacity-20" />
            <p className="text-sm">No panels yet. Add scanners to build your dashboard.</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setAddPanelOpen(true)}>
                <Plus className="h-3 w-3 mr-1" /> Add Scanner Panel
              </Button>
              {dashboards.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => setLoadDialogOpen(true)}>
                  <FolderOpen className="h-3 w-3 mr-1" /> Load Saved
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div
            className="grid gap-3 h-full"
            style={{
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gridAutoRows: 'minmax(250px, 1fr)',
            }}
          >
            {panels.map((panel, i) => {
              const scanner = getScannerById(panel.scannerId);
              if (!scanner) {
                return (
                  <Card key={i} className="flex items-center justify-center bg-card/30 border-dashed border-border/50">
                    <span className="text-xs text-muted-foreground">Scanner not found</span>
                    {isEditing && (
                      <Button variant="ghost" size="icon" className="ml-2 h-6 w-6" onClick={() => removePanel(i)}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </Card>
                );
              }
              return (
                <div
                  key={`${panel.scannerId}-${i}`}
                  style={{
                    gridColumn: `span ${panel.colSpan}`,
                    gridRow: `span ${panel.rowSpan}`,
                  }}
                >
                  <MiniScannerPanel
                    scanner={scanner}
                    results={scannerResults[panel.scannerId] ?? []}
                    columns={scannerColumnMeta[panel.scannerId]}
                    headerColor={panel.headerColor}
                    onSymbolClick={onSymbolSelect}
                    onRemove={() => removePanel(i)}
                    isEditing={isEditing}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Dialogs ── */}
      <AddPanelDialog
        open={addPanelOpen}
        onOpenChange={setAddPanelOpen}
        scanners={allScanners}
        onAdd={addPanel}
        onCreateNew={() => { setAddPanelOpen(false); setCreateScannerOpen(true); }}
      />

      <SaveDashboardDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onSave={handleSave}
        initialName={dashboardName}
        initialDescription={dashboardDesc}
      />

      <CreateScannerDialog
        open={createScannerOpen}
        onOpenChange={setCreateScannerOpen}
        onCreated={handleScannerCreated}
      />

      {/* Load dialog */}
      <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Load Dashboard</DialogTitle>
            <DialogDescription>Select a saved dashboard to load.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {dashboards.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">No saved dashboards yet.</p>
            )}
            {dashboards.map(db => (
              <div
                key={db.id}
                className="flex items-center justify-between p-3 rounded-md border border-border/50 hover:bg-muted/30 transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{db.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {db.panels.length} panels, {db.columns} columns
                    {db.description ? ` — ${db.description}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleLoad(db.id)}>
                    Load
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(db.id)}>
                    <Trash2 className="h-3 w-3 text-red-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLoadDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomDashboard;
